import moment from 'moment';
import firebase from '../config/firebase.config';
import map from 'lodash/map';
import Axios from 'axios';
import httpClient from '../config/httpClient';
import { getServerTime } from './serverTime';
import { getUserTimezoneFromMembersDatabase } from './timezoneResolver';

const database = firebase.firestore();
// const runTransaction = firebase.firestore();

function getTeamData(teamId) {
  if (teamId == null || String(teamId).trim() === '') {
    return Promise.resolve({
      success: false,
      data: null,
    });
  }
  try {
    return database
      .collection('teamDatabase')
      .doc(teamId)
      .get()
      .then((response) => {
        let data;
        if (!response.empty) {
          const docData = response.data();
          data = docData;
        }
        return {
          success: true,
          data: data,
        };
      })
      .catch((error) => {
        return {
          success: false,
          data: error,
        };
      });
  } catch (error) {
    console.log(error);
  }
}

function get7DaysTimes(teamId, day) {
  try {
    return database
      .collection('teamData')
      .doc(teamId)
      .collection('daysData')
      .get()
      .then((response) => {
        let data;
        if (!response.empty) {
          const docData = response.data();
          data = docData;
        }
        return {
          success: true,
          data: data,
        };
      })
      .catch((error) => {
        return {
          success: false,
          data: error,
        };
      });
  } catch (error) {
    console.log(error);
  }
}

function getTimes(teamId, day) {
  try {
    return database
      .collection('teamData')
      .doc(teamId)
      .collection('attendanceTable')
      .doc(day)
      .get()
      .then((response) => {
        let data;
        if (response.exists) {
          const docData = response.data();
          data = docData;
          return {
            success: true,
            data: data,
          };
        } else {
          return {
            success: false,
            data: data,
          };
        }
      })
      .catch((error) => {
        return {
          success: false,
          data: error,
        };
      });
  } catch (error) {
    console.log(error);
  }
}

/**
 * Fetch attendance by month from teamDatabase/{teamId}/members/{memberId}/attendanceByMonth/{formattedMonth}.
 * formattedMonth: lowercase "monthname-year" (e.g. "january-2026").
 * Returns { success, data: { [memberId]: { [dateKey]: { present, startTime, endTime } } } }.
 */
function getTeamAttendanceForMonth(teamId, memberIds, formattedMonth) {
  if (!teamId || !memberIds || !memberIds.length) {
    return Promise.resolve({ success: true, data: {} });
  }
  const fetches = memberIds.map((memberId) =>
    database
      .collection('teamDatabase')
      .doc(teamId)
      .collection('members')
      .doc(memberId)
      .collection('attendanceByMonth')
      .doc(formattedMonth)
      .get()
      .then((snap) => ({ memberId, data: snap.exists ? snap.data() : {} }))
  );
  return Promise.all(fetches)
    .then((results) => {
      const data = {};
      results.forEach(({ memberId, data: docData }) => {
        data[memberId] = docData || {};
      });
      return { success: true, data };
    })
    .catch((error) => ({ success: false, data: error }));
}

function getTopData(teamId, day) {
  try {
    return database
      .collection('teamData')
      .doc(teamId)
      .collection('membersData')
      .doc(day)
      .get()
      .then((response) => {
        let data;
        if (response.exists) {
          const docData = response.data();
          data = docData;
          return {
            success: true,
            data: data,
          };
        } else {
          return {
            success: false,
            data: data,
          };
        }
      })
      .catch((error) => {
        return {
          success: false,
          data: error,
        };
      });
  } catch (error) {
    console.log(error);
  }
}

function getWeekData(teamId, week) {
  let year = moment().isoWeekYear();
  week = `${week}-${year}`;
  try {
    return database
      .collection('teamData')
      .doc(teamId)
      .collection('weeksData')
      .doc(week)
      .get()
      .then((response) => {
        let data;
        if (response.exists) {
          const docData = response.data();
          data = docData;
          return {
            success: true,
            data: data,
          };
        } else {
          return {
            success: false,
            data: data,
          };
        }
      })
      .catch((error) => {
        return {
          success: false,
          data: error,
        };
      });
  } catch (error) {
    console.log(error);
  }
}

/**
 * Sets active company and team on the root membersDatabase document (self-service context).
 */
function updateMemberActiveCompanyAndTeam(userId, companyId, teamId) {
  if (!userId || !companyId || !teamId) {
    return Promise.resolve({
      success: false,
      error: 'Missing userId, companyId, or teamId',
    });
  }
  return database
    .collection('membersDatabase')
    .doc(userId)
    .update({
      activeCompanyId: companyId,
      activeTeamId: teamId,
    })
    .then(() => ({ success: true }))
    .catch((error) => {
      console.error('[updateMemberActiveCompanyAndTeam]', error);
      return { success: false, error };
    });
}

async function sendActiveTeamChangeEmail({ companyName, teamName }) {
  try {
    const functions = firebase.functions();
    const notify = functions.httpsCallable('sendActiveTeamChangeNotification');
    await notify({
      companyName: companyName || '',
      teamName: teamName || '',
    });
    return { success: true };
  } catch (error) {
    console.error('[sendActiveTeamChangeEmail]', error);
    return { success: false, error: error.message };
  }
}

async function sendInviteAcceptedWelcomeEmail(payload) {
  try {
    const functions = firebase.functions();
    const fn = functions.httpsCallable('sendInviteAcceptedWelcome');
    await fn(payload);
    return { success: true };
  } catch (error) {
    console.error('[sendInviteAcceptedWelcomeEmail]', error);
    return { success: false, error: error.message };
  }
}

async function sendInviteDeclinedEmail(payload) {
  try {
    const functions = firebase.functions();
    const fn = functions.httpsCallable('sendInviteDeclined');
    await fn(payload);
    return { success: true };
  } catch (error) {
    console.error('[sendInviteDeclinedEmail]', error);
    return { success: false, error: error.message };
  }
}

function getActiveTeamID(memberID) {
  try {
    return database
      .collection('membersDatabase')
      .doc(memberID)
      .get()
      .then((response) => {
        let data;
        if (!response.empty) {
          const docData = response.data();
          data = docData;
        }
        return {
          success: true,
          data: data,
        };
      })
      .catch((error) => {
        return {
          success: false,
          data: error,
        };
      });
  } catch (error) {
    console.log(error);
  }
}

// async function addTaskList(data)
// async function setActiveTeamId(activeTeamId, activeUserID) {
//   const snapshot = database.collection("membersDatabase").doc(activeUserID);
//   const doc = await snapshot.get();

//   const activeTeam = doc.data();
//   if (activeTeam.activeTeamid) {
//     return database
//       .collection("membersDatabase")
//       .doc(activeUserID)
//       .update({
//         activeTeamid: activeTeamId,
//       })
//       .then(() => {
//         return {
//           success: true,
//         };
//       });
//   } else {
//     return database
//       .collection("membersDatabase")
//       .doc(activeUserID)
//       .set(
//         {
//           activeTeamid: activeTeamId,
//         },
//         { merge: true }
//       )
//       .then(() => {
//         return {
//           success: true,
//         };
//       });
//   }
// }

function setThreshold(companyId, threshold) {
  try {
    return database
      .collection('companyDatabase')
      .doc(companyId)
      .set(
        {
          dailyTimelogThreshold: threshold,
        },
        { merge: true }
      )
      .then(() => {
        return {
          success: true,
        };
      });
  } catch (error) {
    console.log(error);
  }
}
function getThreshold(companyId) {
  if (companyId === '') {
    return {
      success: true,
      data: 0,
    };
  }
  try {
    return database
      .collection('companyDatabase')
      .doc(companyId)
      .get()
      .then((response) => {
        let data = {};
        if (!response.empty) {
          data = response.data();
        }
        return {
          success: true,
          data: data.dailyTimelogThreshold,
        };
      })
      .catch((error) => {
        return {
          success: false,
          data: error,
        };
      });
  } catch (error) {
    console.log(error);
  }
}

function setAlertConfig(companyId, alertConfig) {
  try {
    return database
      .collection('companyDatabase')
      .doc(companyId)
      .set(
        {
          alertConfig: alertConfig,
        },
        { merge: true }
      )
      .then(() => {
        return {
          success: true,
        };
      });
  } catch (error) {
    console.log(error);
    return {
      success: false,
      error: error,
    };
  }
}

function getAlertConfig(companyId) {
  if (companyId === '') {
    return {
      success: true,
      data: {
        enabled: false,
        productivityPercentageThreshold: 50,
        lateLoginAlertMinutes: 30,
      },
    };
  }
  try {
    return database
      .collection('companyDatabase')
      .doc(companyId)
      .get()
      .then((response) => {
        let data = {};
        if (!response.empty) {
          data = response.data();
        }
        // Return default values if alertConfig doesn't exist
        const alertConfig = data.alertConfig || {
          enabled: false,
          productivityPercentageThreshold: 50,
          lateLoginAlertMinutes: 30,
        };
        return {
          success: true,
          data: alertConfig,
        };
      })
      .catch((error) => {
        return {
          success: false,
          data: error,
        };
      });
  } catch (error) {
    console.log(error);
    return {
      success: false,
      data: error,
    };
  }
}

function getTeamNames(userId, companyId) {
  try {
    return database
      .collection('membersDatabase')
      .doc(userId)
      .collection('companyList')
      .doc(companyId)
      .collection('teamList')
      .get()
      .then((querySnapshot) => {
        const tempDoc = querySnapshot.docs.map((doc) => {
          const d = doc.data() || {};
          // Doc id is always the team id; legacy rows may omit activeTeamid in data.
          const activeTeamid =
            d.activeTeamid != null && String(d.activeTeamid).trim() !== ''
              ? d.activeTeamid
              : doc.id;
          return { ...d, activeTeamid };
        });
        let x = tempDoc.filter((a) => {
          if (a?.deleted !== true) {
            return a;
          }
        });
        return {
          success: true,
          data: x,
        };
      })
      .catch((error) => {
        console.log(error);
        return {
          success: false,
          data: error,
        };
      });
  } catch (error) {
    console.log(error);
    return {
      success: false,
      data: error,
    };
  }
}

function getActiveTeam(memberID, activeTeamID, companyId) {
  try {
    return database
      .collection('membersDatabase')
      .doc(memberID)
      .collection('companyList')
      .doc(companyId)
      .collection('teamList')
      .doc(activeTeamID)
      .get()
      .then((response) => {
        let data = {};
        if (!response.empty) {
          data = response.data();
        }
        return {
          success: true,
          data: data,
        };
      })
      .catch((error) => {
        return {
          success: false,
          data: error,
        };
      });
  } catch (error) {
    console.log(error);
  }
}

function getTeamMembers(teamID) {
  if (teamID == null || String(teamID).trim() === '') {
    return Promise.resolve({
      success: false,
      data: [],
    });
  }
  return database
    .collection('teamDatabase')
    .doc(teamID)
    .get()
    .then((response) => {
      let data = [];
      if (!response.empty) {
        if (response.data()) {
          const { memberDetails } = response.data();
          let members = [];
          if (memberDetails) {
            members = map(memberDetails, (value, index) => {
              return {
                ...value,
                userUID: index,
                role: value.userType,
              };
            });
          }
          data = members;
        }
      }
      return {
        success: true,
        data: data,
      };
    })
    .catch((error) => {
      return {
        success: false,
        data: error,
      };
    });
}

function getManagerTeamCollection(teamID, selectedID) {
  return database
    .collection('teamDatabase')
    .doc(teamID)
    .collection('members')
    .doc(selectedID)
    .collection('days')
    .get()
    .then((querySnapshot) => {
      let data = [];
      if (!querySnapshot.empty) {
        querySnapshot.forEach((doc) => {
          data.push({
            date: doc.id,
            ...doc.data(),
          });
        });
      }
      return {
        success: true,
        data: data,
      };
    })
    .catch((error) => {
      return {
        success: false,
        data: error,
      };
    });
}
function getUserTeamCollection(teamID, selectedDate, profileID) {
  return database
    .collection('teamDatabase')
    .doc(teamID)
    .collection('days')
    .doc(selectedDate)
    .collection('members')
    .doc(profileID)
    .get()
    .then((response) => {
      let data = [];
      if (!response.empty) {
        data.push({
          id: response.id,
          ...response.data(),
        });
      }
      return {
        success: true,
        data: data,
      };
    })
    .catch(() => {
      return {
        success: false,
        data: [],
      };
    });
}

async function getTeamsData(payload) {
  const { companyId, userId, role, companyRole } = payload;
  const isCompanyWideAdmin =
    role === 'Admin' || companyRole === 'Admin' || companyRole === 'SuperAdmin';
  if (companyId) {
    return await database
      .collection('teamDatabase')
      .where('ownedBy', '==', companyId)
      .get()
      .then((res) => {
        let data = [];
        if (!res.empty) {
          res.forEach((t) => {
            const cTeam = t.data();
            if (cTeam) {
              // returns all teams of selected company
              if (isCompanyWideAdmin) {
                data.push({
                  docId: t.id,
                  Role: role || companyRole,
                  Team: cTeam.teamName,
                });
              } else {
                // returns only teams that user is member of
                const { memberDetails } = cTeam;
                if (memberDetails) {
                  map(memberDetails, (value, memId) => {
                    if (memId == userId) {
                      data.push({
                        docId: t.id,
                        Role: role,
                        Team: cTeam.teamName,
                      });
                    }
                  });
                }
              }
            }
          });
        }
        return {
          success: true,
          data: data,
        };
      })
      .catch((error) => {
        return {
          success: false,
          data: error,
        };
      });
  }
}

async function getMemberTeamCollection(payload) {
  const { companyId, userId, role } = payload;

  // if (role == 'Member') {
  return await getTeamsData(payload);
  // } else {
  //   return database
  //     .collection("membersDatabase")
  //     .doc(userId)
  //     .collection("teamList")
  //     .get()
  //     .then((response) => {
  //       let data = [];
  //       if (!response.empty) {
  //         response.forEach((doc) => {
  //           data.push({
  //             docId: doc.id,
  //             ...doc.data(),
  //           });
  //         });
  //       }
  //       return {
  //         success: true,
  //         data: data,
  //       };
  //     })
  //     .catch((error) => {
  //       return {
  //         success: false,
  //         data: error,
  //       };
  //     });
  // }
}

async function getMembersTimeLog(
  teamID,
  userID,
  startDate,
  endDate,
  companyId
) {
  const { leaveBalanceService } = await import('./leaveBalance');
  const daysFromDb = await database
    .collection('teamDatabase')
    .doc(teamID)
    .collection('members')
    .doc(userID)
    .collection('days')
    .get()
    .then((data) => {
      const days = [];
      data.forEach(function (doc) {
        const day = doc.id.replaceAll(':', '-');
        if (
          moment(day, 'DD-MM-YYYY').isBetween(
            moment(startDate, 'DD-MM-YYYY'),
            moment(endDate, 'DD-MM-YYYY'),
            'day',
            '[]'
          )
        ) {
          days.push({
            day,
            punchData: doc.data()?.punchinout,
            timeLog: doc.data()?.timelog,
            leave: doc.data()?.leave,
            leaveType: doc.data()?.leaveType,
          });
        }
      });
      return days;
    })
    .catch((err) => {
      return [];
    });

  // Merge approved leave from company (single source) when companyId provided
  if (companyId) {
    try {
      const approvedLeave =
        await leaveBalanceService.getApprovedLeaveForUserInDateRange(
          companyId,
          userID,
          startDate,
          endDate
        );
      const leaveByDate = {};
      approvedLeave.forEach(({ leaveDate, type }) => {
        leaveByDate[leaveDate] = { leave: true, leaveType: type };
      });
      daysFromDb.forEach((d) => {
        const dateKey = d.day.replace(/-/g, ':');
        if (leaveByDate[dateKey]) {
          d.leave = true;
          d.leaveType = leaveByDate[dateKey].leaveType;
        }
      });
    } catch (e) {
      // ignore
    }
  }

  return {
    success: true,
    data: daysFromDb,
  };
}

async function getAllTeamTimeData(teamId, day) {
  return database
    .collection('teamData')
    .doc(teamId)
    .collection('daysData')
    .doc(day)
    .get()
    .then((response) => {
      let data;
      if (response.exists) {
        const docData = response.data();
        data = docData;
        return {
          success: true,
          data: data,
        };
      } else {
        return {
          success: false,
          data: data,
        };
      }
    })
    .catch((error) => {
      return {
        success: false,
        data: error,
      };
    });
}

async function getDateRangeTimeData(start, end, teamId) {
  console.log(start, end, teamId, 'getDateRangeTimeData');
  try {
    const response = await httpClient.post('/dateRangeForTeam', {
      start,
      end,
      teamId,
    });
    const res = response.data;
    console.log('data in getDateRangeTimeData', res.result);
    if (res.success) {
      return { success: true, data: res.result };
    }
    return { success: false, data: res.message };
  } catch (err) {
    console.log(err);
    return { success: false, data: err };
  }
}

async function getAllTeamWeekTimeData(teamId, day) {
  return database
    .collection('teamData')
    .doc(teamId)
    .collection('lastWeekData')
    .doc(day)
    .get()
    .then((response) => {
      let data;
      if (response.exists) {
        const docData = response.data();
        data = docData;
        return {
          success: true,
          data: data,
        };
      } else {
        return {
          success: false,
          data: data,
        };
      }
    })
    .catch((error) => {
      return {
        success: false,
        data: error,
      };
    });
}

async function getAllTeamMonthTimeData(teamId, day) {
  return database
    .collection('teamData')
    .doc(teamId)
    .collection('lastMonthData')
    .doc(day)
    .get()
    .then((response) => {
      let data;
      if (response.exists) {
        const docData = response.data();
        data = docData;
        return {
          success: true,
          data: data,
        };
      } else {
        return {
          success: false,
          data: data,
        };
      }
    })
    .catch((error) => {
      return {
        success: false,
        data: error,
      };
    });
}

async function getAllTeamTimeLog(teamID) {
  const snapshot = await database
    .collection('teamDatabase')
    .doc(teamID)
    .collection('members')
    .get();
  let data = [];
  if (!snapshot.empty) {
    const promiseAr = [];
    // Iterate through each member and fetch their respective time logs
    snapshot.forEach((doc) => {
      // `doc.id` is the memberId
      promiseAr.push(
        database
          .collection('teamDatabase')
          .doc(teamID)
          .collection('members')
          .doc(doc.id) // store the member id
          .collection('days')
          .get()
          .then((res) => ({ memberId: doc.id, res })) // Attach memberId to the response
      );
    });
    const timeLogs = await Promise.all(promiseAr);
    timeLogs.forEach(({ memberId, res }) => {
      res.forEach((d) => {
        data.push({
          ...d.data(),
          date: d.id, // Assuming the `d.id` is the date
          memberId, // Add memberId to each time log entry
        });
      });
    });
    return {
      success: true,
      data, // This now contains both memberId and time log data
    };
  } else {
    return { success: true, data: [] };
  }
}

async function addProject(payload) {
  const data = {
    project_name: payload.project,
    createdBy: payload.createdBy,
  };
  return database
    .collection('companyDatabase')
    .doc(payload.companyId)
    .collection('projects')
    .doc()
    .set(data, {
      merge: true,
    });
}

async function getProjects(companyId) {
  const snapshot = await database
    .collection('companyDatabase')
    .doc(companyId.companyId)
    .collection('projects')
    .get()
    .catch((error) => {
      console.log('Error getting documents: ', error);
    });

  const data = snapshot.docs.map((doc) => {
    const docData = doc.data();
    return {
      data: docData,
      id: doc.id,
    };
  });
  return {
    success: true,
    data: data,
  };
}

// async function getProjectId(memberId, companyId) {
//   //gets a snapshot of the projects of collection projectList
//   const snapshot = await database
//     .collection("membersDatabase")
//     .doc(memberId)
//     .collection("companyList")
//     .doc(companyId)
//     .collection("projectList")
//     .get()
//     .catch((error) => {
//       console.log("Error getting documents: ", error);
//     });

//   // Get id's of the projects
//   const Ids = snapshot.docs?.map(doc => doc.id);

//   // Get names of the projects
//   const Names = snapshot.docs?.map(doc => doc.data());

//   const NameId = Ids.map((id, index) => {
//     return {
//       id,
//       name: Names[index],
//     };
//   })
//   return {
//     success: true,
//     data: NameId
//   }
// }
async function getProjectId(memberId, companyId) {
  //gets a snapshot of the projects from the companyDatabase
  const snapshot = await database
    .collection('companyDatabase')
    .doc(companyId)
    .collection('projects')
    .get()
    .catch((error) => {
      console.log('Error getting documents: ', error);
    });

  // Get id's of the projects
  const Ids = snapshot.docs?.map((doc) => doc.data().p_id);

  return {
    success: true,
    data: Ids,
  };
}

async function getTopProjects(projectId, rawData, arr) {
  //gets a snapshot of the projects from the companyDatabase
  const data = await database
    .collection('companyDatabase')
    .doc(localStorage.getItem('cid'))
    .collection('projects')
    .doc(projectId)
    .get();
  const dataRaw = data.data();
  const objIndex = rawData.findIndex(
    (obj) => obj.projectName == dataRaw.project_name
  );
  if (objIndex == -1) {
    rawData.push({
      projectName: dataRaw.project_name,
      time:
        parseInt(`${arr?.taskMinutes}`) < 10
          ? parseFloat(`${arr.taskHours}.0${arr.taskMinutes}`)
          : parseFloat(`${arr.taskHours}.${arr.taskMinutes}`),
      minuites: parseInt(`${arr?.taskMinutes}`),
      hours: parseInt(`${arr?.taskHours}`),
      color: '#' + Math.floor(Math.random() * 16777215).toString(16),
    });
  } else {
    rawData[objIndex].time +=
      parseInt(`${arr?.taskMinutes}`) < 10
        ? parseFloat(`${arr.taskHours}.0${arr.taskMinutes}`)
        : parseFloat(`${arr.taskHours}.${arr.taskMinutes}`);
    rawData[objIndex].minuites += parseInt(`${arr.taskMinutes}`);
    rawData[objIndex].hours += parseInt(`${arr.taskHours}`);
    if (rawData[objIndex].minuites > 59) {
      rawData[objIndex].minuites = 0;
      ++rawData[objIndex].hours; // rawData[objIndex].hours += 1;
    }
  }
}

async function getTeamsId(companyId) {
  //gets a snapshot of the teams from the companyDatabase
  const snapshot = await database
    .collection('companyDatabase')
    .doc(companyId)
    .collection('teams')
    .get()
    .catch((error) => {
      console.log('Error getting documents: ', error);
      return null;
    });

  if (!snapshot) {
    return {
      success: false,
      data: [],
    };
  }

  // Get id's of the teams
  const Ids = [];
  snapshot.docs?.forEach((doc) => Ids.push(doc.data()));

  return {
    success: true,
    data: Ids,
  };
}

// async function getTeamsId(memberId, companyId) {
//   //gets a snapshot of the teams of collection teamList
//   const snapshot = await database
//     .collection("membersDatabase")
//     .doc(memberId)
//     .collection("companyList")
//     .doc(companyId)
//     .collection("teamList")
//     .get()
//     .catch((error) => {
//       console.log("Error getting documents: ", error);
//     });

//   // Get id's of the teams
//   const Ids = snapshot.docs?.map(doc => doc.id);

//   // Get names of the teams
//   const Names = snapshot.docs?.map(doc => doc.data().Team);

//   const NameId = Ids.map((id, index) => {
//     return {
//       id,
//       name: Names[index],
//     };
//   })
//   return {
//     success: true,
//     data: NameId,
//   };
// }

async function getCompaniesId(memberId) {
  //gets a snapshot of the companies of collection teamList
  try {
    const snapshot = await database
      .collection('membersDatabase')
      .doc(memberId)
      .collection('companyList')
      .get()
      .catch((error) => {
        console.log('Error getting documents: ', error);
      });

    // Get id's of the companies
    const Ids = snapshot.docs?.map((doc) => doc.id);

    // Get names of the companies
    const Names = snapshot.docs?.map((doc) => doc.data().companyName);

    const NameId = Ids.map((id, index) => {
      return {
        id,
        name: Names[index],
      };
    });
    return {
      success: true,
      data: NameId,
    };
  } catch (error) {
    console.log(error);
  }
}

async function updateProject({ team, projectId, title, companyId }) {
  const snapshot = await database
    .collection('companyDatabase')
    .doc(companyId)
    .collection('projects')
    .doc(projectId)
    .update({ project_name: title })
    .catch((err) => {
      console.log('err', err);
    });
  return snapshot;
}

async function addTaskList(data) {
  const taskRef = database.collection('taskDatabase').doc();
  return database
    .runTransaction(async (transaction) => {
      const cityRef = database
        .collection('companyDatabase')
        .doc(data.companyId)
        .collection('projects')
        .doc(data.value.team);
      const doc = await transaction.get(cityRef);

      const project = doc.data();
      const arr = [];
      arr.push(taskRef.id);

      if (project.taskList) {
        project.taskList.map((task) => arr.push(task));
        const newA = project.taskList.filter(
          (task) => task.toLowerCase() === data.value.task.toLowerCase()
        );
        if (newA.length === 0) {
          const cityRef = database
            .collection('companyDatabase')
            .doc(data.companyId)
            .collection('projects')
            .doc(data.value.team);
          await transaction.update(cityRef, { taskList: arr });
        }
      } else {
        const cityRef = database
          .collection('companyDatabase')
          .doc(data.companyId)
          .collection('projects')
          .doc(data.value.team);
        await transaction.update(cityRef, { taskList: [taskRef.id] });
      }
      transaction.set(taskRef, {
        Title: data?.value?.task,
        Description: data?.value?.summery,
        Priority: data?.value?.priority,
        Assignee: data?.value?.assignee,
        Status: data?.value?.status,
        ProjectID: data?.value?.team,
      });
    })
    .then((data) => {
      return { message: 'Task added successfully', data: data };
    })
    .catch((err) => {
      // This will be an "population is too big" error.
      console.error(err);
    });
}

async function getTaskList({ teamId, activeUserId }) {
  if (activeUserId) {
    const data = await database
      .collection('teamDatabase')
      .doc(teamId)
      .collection('members')
      .doc(activeUserId)
      .get();

    return {
      success: true,
      data: [data.data().inProgressTaskList],
    };
  }
  return database
    .collection('teamDatabase')
    .doc(teamId)
    .collection('members')
    .get()
    .then(async (res) => {
      const promiseAr = [];
      res.forEach((doc) => {
        promiseAr.push(
          database
            .collection('teamDatabase')
            .doc(teamId)
            .collection('members')
            .doc(doc.id)
            .get()
        );
      });
      const data = [];
      const r = await Promise.all(promiseAr);
      r.forEach((res) => {
        data.push(res.data().inProgressTaskList);
      });

      return {
        success: true,
        data,
      };
    })
    .catch((err) => console.log(err));
}

async function deleteProject({ teamId, projectId, title, companyId }) {
  return database
    .collection('companyDatabase')
    .doc(companyId.companyId)
    .collection('projects')
    .doc(projectId)
    .update({ deleted: true })
    .catch((err) => {
      console.log('err', err);
    });
}

async function getAllMembers(emails) {
  let count = 0;
  let startIndex = 0;
  let endIndex = 10;
  let emsLength = emails.length;
  let existed = [];
  do {
    let emsData = emails.slice(startIndex, endIndex);
    startIndex = endIndex;
    endIndex = endIndex + 10;
    let querySnapshot = await database
      .collection('membersDatabase')
      .where('accountEmailid', 'in', emsData)
      .get();
    if (querySnapshot) {
      if (!querySnapshot.empty) {
        let a = querySnapshot.docs.map((doc) => doc.data());
        existed = [...existed, ...a];
      }
    }
  } while (endIndex < emsLength + 1);
  return existed;
}

/**
 * Get all team members data team-wise
 * @param {*} teams
 * @returns
 */
async function getAllTeamMembers({ teams, authUser, role }) {
  try {
    let tempTeams = teams;
    let finalTeamData = [];
    let count = 0;

    do {
      let teamId = tempTeams[count].value;
      let teamName = tempTeams[count].label;
      let { success, data } = await getTeamMembers(teamId);
      if (success) {
        // Returns only member details
        // if (role == 'Member') {
        //   data = data.filter(mem => mem.userUID == authUser);
        // }
        let x = data.filter((a) => {
          if (a?.deleted !== true) {
            return a;
          }
        });
        finalTeamData.push({
          team: teamId,
          teamName: teamName,
          members: x,
          noOfMembers: data.length,
        });
      }

      count = count + 1;
    } while (count < tempTeams.length);

    return { success: true, data: [...finalTeamData] };
  } catch (e) {
    return { success: false, data: e };
  }
}

// Function to get team timelog data
async function getTeamTimelogData(teamId, startDate, endDate) {
  try {
    const response = await httpClient.get('/generateConsolidatedTimelogExcel', {
      params: { teamId, startDate, endDate },
      responseType: 'blob',
    });

    if (response.status === 200) {
      // Extract filename from response headers
      const contentDisposition = response.headers['content-disposition'];
      let fileName = 'timelog_data.xlsx';

      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
        if (fileNameMatch) {
          fileName = fileNameMatch[1];
        }
      }

      return {
        success: true,
        data: {
          blob: response.data,
          fileName: fileName,
        },
      };
    } else {
      return {
        success: false,
        error: 'Failed to download timelog data',
      };
    }
  } catch (error) {
    console.log('Error getting team timelog data: ', error);
    return {
      success: false,
      error: error.message || 'Error downloading timelog data',
    };
  }
}

// Function to save active timer session - NEW: Uses separate activeSessions collection
async function saveActiveTimerSession(teamId, memberId, sessionData) {
  try {
    const docRef = database
      .collection('teamDatabase')
      .doc(teamId)
      .collection('members')
      .doc(memberId)
      .collection('activeSessions')
      .doc('currentSession');

    // Add session metadata
    const sessionWithMeta = {
      ...sessionData,
      createdAt: firebase.firestore.FieldValue.serverTimestamp(),
      lastHeartbeat: firebase.firestore.FieldValue.serverTimestamp(),
      source: sessionData.source || 'web',
    };

    await docRef.set(sessionWithMeta);

    return {
      success: true,
      sessionId: sessionData.sessionId,
    };
  } catch (error) {
    console.log('Error saving active timer session: ', error);
    return {
      success: false,
      error: error.message,
    };
  }
}

// Function to get active timer session - NEW: Uses separate activeSessions collection
async function getActiveTimerSession(teamId, memberId) {
  try {
    console.log('🟣 getActiveTimerSession: Fetching active session', {
      teamId,
      memberId,
      timestamp: new Date().toISOString(),
    });

    const docRef = database
      .collection('teamDatabase')
      .doc(teamId)
      .collection('members')
      .doc(memberId)
      .collection('activeSessions')
      .doc('currentSession');

    console.log('🟣 getActiveTimerSession: Document path', {
      path: `teamDatabase/${teamId}/members/${memberId}/activeSessions/currentSession`,
      docRefPath: docRef.path,
    });

    const doc = await docRef.get();

    console.log('🟣 getActiveTimerSession: Document fetch result', {
      exists: doc.exists,
      hasData: doc.exists ? !!doc.data() : false,
    });

    if (doc.exists) {
      const sessionData = doc.data();

      console.log('🟣 getActiveTimerSession: Session data found', {
        sessionId: sessionData.sessionId,
        startTime: sessionData.startTime,
        sessionTimezone: sessionData.sessionTimezone,
        projectId: sessionData.projectId,
        taskName: sessionData.taskName,
        isActive: sessionData.isActive,
        source: sessionData.source,
        createdAt: sessionData.createdAt
          ? sessionData.createdAt.toDate()
          : null,
        fullSessionData: sessionData,
      });

      return {
        success: true,
        data: sessionData,
        exists: true,
      };
    } else {
      console.log('🟣 getActiveTimerSession: No active session found');
      return {
        success: true,
        data: null,
        exists: false,
      };
    }
  } catch (error) {
    console.error(
      '🟣 getActiveTimerSession: Error getting active timer session',
      {
        error: error.message,
        stack: error.stack,
        teamId,
        memberId,
      }
    );
    return {
      success: false,
      error: error.message,
      exists: false,
    };
  }
}

// Function to clear active timer session - NEW: Uses separate activeSessions collection
async function clearActiveTimerSession(teamId, memberId) {
  try {
    const docRef = database
      .collection('teamDatabase')
      .doc(teamId)
      .collection('members')
      .doc(memberId)
      .collection('activeSessions')
      .doc('currentSession');

    await docRef.delete();

    return {
      success: true,
    };
  } catch (error) {
    console.log('Error clearing active timer session: ', error);
    return {
      success: false,
      error: error.message,
    };
  }
}

// Function to save timelog entry
async function saveTimelogEntry(teamId, memberId, dateId, timelogData) {
  try {
    const docRef = database
      .collection('teamDatabase')
      .doc(teamId)
      .collection('members')
      .doc(memberId)
      .collection('days')
      .doc(dateId);

    return database.runTransaction(async (transaction) => {
      const doc = await transaction.get(docRef);
      let existingTimelog = {};

      if (doc.exists && doc.data().timelog) {
        existingTimelog = doc.data().timelog;
      }

      const updatedTimelog = {
        ...existingTimelog,
        ...timelogData,
      };

      if (doc.exists) {
        await transaction.update(docRef, {
          timelog: updatedTimelog,
        });
      } else {
        await transaction.set(docRef, {
          timelog: updatedTimelog,
        });
      }

      return { success: true };
    });
  } catch (error) {
    console.log('Error saving timelog entry: ', error);
    return {
      success: false,
      error: error.message,
    };
  }
}

// Function to save timelog entries to multiple days (for split sessions) - NEW
async function saveTimelogEntriesToMultipleDays(
  teamId,
  memberId,
  splitEntries
) {
  try {
    console.log(
      '🔵 saveTimelogEntriesToMultipleDays: Starting batch save operation',
      {
        teamId,
        memberId,
        entriesCount: splitEntries.length,
        entries: splitEntries.map((entry) => ({
          date: entry.date,
          timestampKey: entry.timestampKey,
          taskHours: entry.entry.taskHours,
          taskMinutes: entry.entry.taskMinutes,
          isSplit: entry.entry.isSplit,
          splitIndex: entry.entry.splitIndex,
        })),
      }
    );

    // Use batch write for atomic operations across multiple days
    const batch = database.batch();
    let savedCount = 0;
    const operationDetails = [];

    for (let i = 0; i < splitEntries.length; i++) {
      const splitEntry = splitEntries[i];
      const { date, timestampKey, entry } = splitEntry;

      console.log(
        `🔵 saveTimelogEntriesToMultipleDays: Processing entry ${i + 1}/${
          splitEntries.length
        }`,
        {
          entryIndex: i + 1,
          date,
          timestampKey,
          entry: {
            taskHours: entry.taskHours,
            taskMinutes: entry.taskMinutes,
            projectId: entry.projectId,
            taskName: entry.taskName,
            isSplit: entry.isSplit,
            splitIndex: entry.splitIndex,
          },
        }
      );

      const docRef = database
        .collection('teamDatabase')
        .doc(teamId)
        .collection('members')
        .doc(memberId)
        .collection('days')
        .doc(date);

      console.log(
        `🔵 saveTimelogEntriesToMultipleDays: Document path for entry ${i + 1}`,
        {
          path: `teamDatabase/${teamId}/members/${memberId}/days/${date}`,
          docRef: docRef.path,
        }
      );

      // Get existing timelog data for this day
      console.log(
        `🔵 saveTimelogEntriesToMultipleDays: Fetching existing document for entry ${
          i + 1
        }`
      );
      const doc = await docRef.get();
      let existingTimelog = {};

      if (doc.exists && doc.data().timelog) {
        existingTimelog = doc.data().timelog;
        console.log(
          `🔵 saveTimelogEntriesToMultipleDays: Found existing timelog for entry ${
            i + 1
          }`,
          {
            existingKeys: Object.keys(existingTimelog),
            existingCount: Object.keys(existingTimelog).length,
          }
        );
      } else {
        console.log(
          `🔵 saveTimelogEntriesToMultipleDays: No existing timelog found for entry ${
            i + 1
          }`
        );
      }

      // Add the new entry to existing timelog
      const updatedTimelog = {
        ...existingTimelog,
        [timestampKey]: entry,
      };

      console.log(
        `🔵 saveTimelogEntriesToMultipleDays: Updated timelog for entry ${
          i + 1
        }`,
        {
          newTimestampKey: timestampKey,
          totalKeysAfterUpdate: Object.keys(updatedTimelog).length,
          entryBeingAdded: {
            taskHours: entry.taskHours,
            taskMinutes: entry.taskMinutes,
            projectId: entry.projectId,
            taskName: entry.taskName,
          },
        }
      );

      // Add to batch
      const operation = {
        entryIndex: i + 1,
        date,
        timestampKey,
        docExists: doc.exists,
        operation: doc.exists ? 'UPDATE' : 'SET',
      };

      if (doc.exists) {
        batch.update(docRef, {
          timelog: updatedTimelog,
        });
        console.log(
          `🔵 saveTimelogEntriesToMultipleDays: Added UPDATE operation to batch for entry ${
            i + 1
          }`
        );
      } else {
        batch.set(docRef, {
          timelog: updatedTimelog,
        });
        console.log(
          `🔵 saveTimelogEntriesToMultipleDays: Added SET operation to batch for entry ${
            i + 1
          }`
        );
      }

      operationDetails.push(operation);
      savedCount++;
    }

    console.log(
      '🔵 saveTimelogEntriesToMultipleDays: About to commit batch operations',
      {
        totalOperations: savedCount,
        operationDetails,
        batchSize: savedCount,
      }
    );

    // Commit all writes atomically
    await batch.commit();

    console.log(
      '🔵 saveTimelogEntriesToMultipleDays: Batch commit successful',
      {
        savedCount,
        message: `Successfully saved ${savedCount} timelog entries`,
      }
    );

    return {
      success: true,
      savedCount,
      message: `Successfully saved ${savedCount} timelog entries`,
    };
  } catch (error) {
    console.error(
      '🔵 saveTimelogEntriesToMultipleDays: Error saving split timelog entries',
      {
        error: error.message,
        stack: error.stack,
        teamId,
        memberId,
        entriesCount: splitEntries ? splitEntries.length : 0,
      }
    );
    return {
      success: false,
      error: error.message,
      savedCount: 0,
    };
  }
}

// Get active projects for timer (not deleted)
async function getActiveProjects(companyId) {
  try {
    const snapshot = await database
      .collection('companyDatabase')
      .doc(companyId)
      .collection('projects')
      .get();

    const projects = snapshot.docs.map((doc) => ({
      id: doc.id,
      name: doc.data().project_name,
      taskList: doc.data().taskList || [],
      ...doc.data(),
    }));

    console.log('WebTimeTracking : Projects in team.js', projects);
    return {
      success: true,
      data: projects,
    };
  } catch (error) {
    console.error('Error getting active projects:', error);
    return {
      success: false,
      error: error.message,
      data: [],
    };
  }
}

// Check for existing active timer session (for cross-platform validation) - NEW: Uses activeSessions collection
async function checkExistingActiveSession(teamId, memberId) {
  try {
    const docRef = database
      .collection('teamDatabase')
      .doc(teamId)
      .collection('members')
      .doc(memberId)
      .collection('activeSessions')
      .doc('currentSession');

    const doc = await docRef.get();

    if (doc.exists) {
      const session = doc.data();
      return {
        success: true,
        data: session,
        exists: true,
      };
    } else {
      return {
        success: true,
        data: null,
        exists: false,
      };
    }
  } catch (error) {
    console.log('Error checking existing active session: ', error);
    return {
      success: false,
      error: error.message,
      exists: false,
    };
  }
}

// Get tasks for a specific project assigned to user and not completed
async function getProjectTasks(projectId, userId, companyId) {
  try {
    // First get the project to get the taskList
    const projectDoc = await database
      .collection('companyDatabase')
      .doc(companyId)
      .collection('projects')
      .doc(projectId)
      .get();

    if (!projectDoc.exists) {
      return {
        success: false,
        error: 'Project not found',
        data: [],
      };
    }

    const projectData = projectDoc.data();
    const taskList = projectData.taskList || [];

    if (taskList.length === 0) {
      return {
        success: true,
        data: [],
      };
    }

    // Get tasks from taskDatabase and filter
    const taskPromises = taskList.map((taskId) =>
      database.collection('taskDatabase').doc(taskId).get()
    );

    const taskDocs = await Promise.all(taskPromises);

    const tasks = taskDocs
      .filter((doc) => doc.exists)
      .map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }))
      .filter((task) => {
        // Filter: assigned to current user AND not completed
        return task.Assignee === userId && task.Status !== 'DONE';
      });

    return {
      success: true,
      data: tasks,
    };
  } catch (error) {
    console.error('Error getting project tasks:', error);
    return {
      success: false,
      error: error.message,
      data: [],
    };
  }
}

// Function to get user timezone from database (membersDatabase)
async function getUserTimezoneFromDB(teamId, userId) {
  try {
    console.log('🟨 getUserTimezoneFromDB: Fetching user timezone', {
      teamId,
      userId,
      timestamp: new Date().toISOString(),
    });
    const result = await getUserTimezoneFromMembersDatabase(userId);
    console.log('🟨 getUserTimezoneFromDB: membersDatabase response', {
      userId,
      source: result.source,
      timezone: result.timezone,
      success: result.success,
      error: result.error || null,
    });
    return result;
  } catch (error) {
    console.error(
      '🟨 getUserTimezoneFromDB: Error getting user timezone, using IST fallback',
      {
        error: error.message,
        stack: error.stack,
        teamId,
        userId,
        fallbackTimezone: 'Asia/Kolkata',
      }
    );

    return {
      success: true,
      timezone: 'Asia/Kolkata',
      source: 'fallback',
    };
  }
}

// Function to save punch in/out data
async function savePunchData(
  teamId,
  memberId,
  punchTime,
  punchData,
  companyId
) {
  try {
    console.log('🟣 savePunchData: Starting punch data save', {
      teamId,
      memberId,
      punchTime: punchTime ? punchTime.toISOString() : 'undefined',
      punchState: punchData.state,
      timestamp: new Date().toISOString(),
    });

    // Get user timezone from database first
    const timezoneResponse = await getUserTimezoneFromDB(teamId, memberId);

    if (!timezoneResponse.success) {
      console.error('🟣 savePunchData: Failed to get user timezone', {
        error: timezoneResponse.error,
      });
      return {
        success: false,
        error: timezoneResponse.error,
      };
    }

    const userTimezone = timezoneResponse.timezone;
    console.log('🟣 savePunchData: Using database timezone', {
      timezone: userTimezone,
    });

    const now = punchTime || (await getServerTime());

    // Use database timezone to generate date key
    const { getDateKey, formatTimezoneString } = require('../utils/timeUtils');
    const dateId = getDateKey(now, userTimezone);

    console.log('🟣 savePunchData: Generated date key', {
      dateId,
      timezone: userTimezone,
      punchTime: now.toISOString(),
    });

    // Create the timestamp key using the timezone-specific format
    const timestampKey = formatTimezoneString(now, userTimezone);

    const docRef = database
      .collection('teamDatabase')
      .doc(teamId)
      .collection('members')
      .doc(memberId)
      .collection('days')
      .doc(dateId);

    console.log('🟣 savePunchData: Document path', {
      path: `teamDatabase/${teamId}/members/${memberId}/days/${dateId}`,
      docRefPath: docRef.path,
      timestampKey,
    });

    return database.runTransaction(async (transaction) => {
      const doc = await transaction.get(docRef);
      let existingPunchData = {};

      if (doc.exists && doc.data().punchinout) {
        existingPunchData = doc.data().punchinout;
        console.log('🟣 savePunchData: Found existing punch data', {
          existingKeys: Object.keys(existingPunchData),
          existingCount: Object.keys(existingPunchData).length,
        });
      } else {
        console.log('🟣 savePunchData: No existing punch data found');
      }

      // Add new punch data to existing punch data
      const updatedPunchData = {
        ...existingPunchData,
        [timestampKey]: punchData,
      };

      const updateData = {
        punchinout: updatedPunchData,
        lastPunchinoutStatus: punchData.state,
      };

      console.log('🟣 savePunchData: Updating document', {
        operation: doc.exists ? 'UPDATE' : 'SET',
        newPunchState: punchData.state,
        totalPunchEntries: Object.keys(updatedPunchData).length,
      });

      if (doc.exists) {
        await transaction.update(docRef, updateData);
      } else {
        await transaction.set(docRef, updateData);
      }

      console.log('🟣 savePunchData: Punch data saved successfully');
      return { success: true };
    });
  } catch (error) {
    console.error('🟣 savePunchData: Error saving punch data', {
      error: error.message,
      stack: error.stack,
      teamId,
      memberId,
    });
    return {
      success: false,
      error: error.message,
    };
  }
}

// Function to get current punch status
async function getCurrentPunchStatus(teamId, memberId) {
  try {
    console.log('🟣 getCurrentPunchStatus: Getting punch status', {
      teamId,
      memberId,
      timestamp: new Date().toISOString(),
    });

    // Get user timezone from database first
    const timezoneResponse = await getUserTimezoneFromDB(teamId, memberId);

    if (!timezoneResponse.success) {
      console.error('🟣 getCurrentPunchStatus: Failed to get user timezone', {
        error: timezoneResponse.error,
      });
      return {
        success: false,
        error: timezoneResponse.error,
      };
    }

    const userTimezone = timezoneResponse.timezone;
    console.log('🟣 getCurrentPunchStatus: Using database timezone', {
      timezone: userTimezone,
    });

    // Use database timezone to generate date key
    const { getDateKey } = require('../utils/timeUtils');
    const serverTime = await getServerTime();
    const dateId = getDateKey(serverTime, userTimezone);

    console.log('🟣 getCurrentPunchStatus: Generated date key', {
      dateId,
      timezone: userTimezone,
    });

    const docRef = database
      .collection('teamDatabase')
      .doc(teamId)
      .collection('members')
      .doc(memberId)
      .collection('days')
      .doc(dateId);

    console.log('🟣 getCurrentPunchStatus: Document path', {
      path: `teamDatabase/${teamId}/members/${memberId}/days/${dateId}`,
      docRefPath: docRef.path,
    });

    const doc = await docRef.get();

    if (doc.exists && doc.data().lastPunchinoutStatus) {
      console.log('🟣 getCurrentPunchStatus: Punch status found', {
        lastPunchinoutStatus: doc.data().lastPunchinoutStatus,
        hasPunchinout: !!doc.data().punchinout,
      });

      return {
        success: true,
        data: {
          lastPunchinoutStatus: doc.data().lastPunchinoutStatus,
          punchinout: doc.data().punchinout || {},
        },
      };
    } else {
      console.log('🟣 getCurrentPunchStatus: No punch status found for today');
      return {
        success: false,
        data: null,
      };
    }
  } catch (error) {
    console.error(
      '🟣 getCurrentPunchStatus: Error getting current punch status',
      {
        error: error.message,
        stack: error.stack,
        teamId,
        memberId,
      }
    );
    return {
      success: false,
      error: error.message,
    };
  }
}

async function getTeamTimezone(teamId) {
  try {
    const snap = await database.collection('teamDatabase').doc(teamId).get();
    return { success: true, timeZone: snap.data()?.timeZone ?? null };
  } catch (error) {
    return { success: false, timeZone: null, error: error.message };
  }
}

async function updateTeamTimezone(teamId, timeZone) {
  try {
    await database
      .collection('teamDatabase')
      .doc(teamId)
      .set({ timeZone }, { merge: true });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
}

// Team-scoped alertConfig + workingSchedule — no company-level fallback, so
// saving/resetting these two fields together (one write) is what turns the
// Late/Early Login Alert and team-wise Productivity Alert on or off for a
// given team. Mirrors productivityApps.js's save/reset shape, kept here
// instead since these are "team settings" like timeZone, not a parallel
// domain of their own.
// Accepts either or both fields — Team Settings only ever edits
// workingSchedule, Alerts & Notifications only ever edits alertConfig, and
// each should be able to save without touching (or racing on) the field
// the other section owns.
async function saveTeamAttendanceSettings(
  teamId,
  { alertConfig, workingSchedule } = {}
) {
  const payload = {};
  if (alertConfig !== undefined) payload.alertConfig = alertConfig;
  if (workingSchedule !== undefined) payload.workingSchedule = workingSchedule;
  try {
    await database
      .collection('teamDatabase')
      .doc(teamId)
      .set(payload, { merge: true });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
}

async function resetTeamAttendanceSettings(teamId) {
  try {
    await database.collection('teamDatabase').doc(teamId).update({
      alertConfig: firebase.firestore.FieldValue.delete(),
      workingSchedule: firebase.firestore.FieldValue.delete(),
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
}

export default {
  getActiveTeamID,
  updateMemberActiveCompanyAndTeam,
  sendActiveTeamChangeEmail,
  sendInviteAcceptedWelcomeEmail,
  sendInviteDeclinedEmail,
  getTeamNames,
  getTimes,
  getTeamAttendanceForMonth,
  getWeekData,
  get7DaysTimes,
  getActiveTeam,
  getTeamMembers,
  getUserTeamCollection,
  getManagerTeamCollection,
  getMemberTeamCollection,
  getMembersTimeLog,
  getAllMembers,
  getAllTeamMembers,
  getAllTeamTimeLog,
  getAllTeamTimeData,
  getDateRangeTimeData,
  getAllTeamWeekTimeData,
  getAllTeamMonthTimeData,
  addProject,
  getProjects,
  getTopProjects,
  getProjectId,
  getTaskList,
  addTaskList,
  updateProject,
  deleteProject,
  getTeamData,
  getTeamsId,
  getCompaniesId,
  getTopData,
  // setActiveTeamId,
  setThreshold,
  getThreshold,
  setAlertConfig,
  getAlertConfig,
  getTeamTimelogData,
  saveActiveTimerSession,
  getActiveTimerSession,
  clearActiveTimerSession,
  saveTimelogEntry,
  saveTimelogEntriesToMultipleDays,
  getActiveProjects,
  getProjectTasks,
  checkExistingActiveSession,
  savePunchData,
  getCurrentPunchStatus,
  getUserTimezoneFromDB,
  getTeamTimezone,
  updateTeamTimezone,
  saveTeamAttendanceSettings,
  resetTeamAttendanceSettings,
};
